import React, { useEffect, useState, useCallback } from 'react';
import { FleetSummaryItem, Vessel } from '../core/_models';
import { getFleetSummary } from '../core/_requests';
import VesselLandingLink from '../../../shared/components/VesselLandingLink';
import { showErrorToast } from '../../../shared/utils/apiErrorMessage';

type StatusConfig = {
  className: string
  style?: React.CSSProperties
  image?: string
  label: string
}

interface VesselsViewProps {
  setFilteredVessels: React.Dispatch<React.SetStateAction<Vessel[]>>;
  onVesselCountChange?: (count: number) => void;
}

/**
 * Renders the vessel list panel on the Home dashboard.
 *
 * Data source: GET /api/dashboard/fleet-summary (one request, backend-scoped).
 * Full vessel details are NOT fetched here — they load lazily when a user
 * clicks a map marker or vessel name.
 */
const VesselsView: React.FC<VesselsViewProps> = ({ setFilteredVessels, onVesselCountChange }) => {
  const [summary, setSummary] = useState<FleetSummaryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track which accordion groups are open — key = groupKey string
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (key: string) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFleetSummary();
      setSummary(data);
    } catch (err: any) {
      console.error('Failed to fetch fleet summary:', err);
      const msg = showErrorToast(err, 'Failed to load fleet vessels.');
      setError(msg || 'Failed to load fleet vessels.');
      setSummary([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Single fetch on mount — backend handles role-based scope
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Push enriched Vessel[] up to parent (MapView needs lat/lng on the Vessel shape)
  useEffect(() => {
    const vessels: Vessel[] = summary.map((item) => ({
      id: item.id,
      fleet_name: item.fleetName,
      imoNumber: item.imoNumber,
      vesselType: item.vesselType,
      status: item.status ?? 'Unknown',
      port: item.nextPort ?? 'Unknown',
      eta: item.eta ?? 'Unknown',
      latitude: item.latitude ?? '',
      longitude: item.longitude ?? '',
      companyGroupAdmin: item.companyGroupAdminId != null
        ? { id: item.companyGroupAdminId } as any
        : undefined,
      companyAdmin: item.companyAdminId != null
        ? { id: item.companyAdminId } as any
        : undefined,
    }));

    setFilteredVessels(vessels);
    onVesselCountChange?.(vessels.length);
  }, [summary, setFilteredVessels, onVesselCountChange]);

  const getStatusConfig = (status: string | null): StatusConfig => {
    switch (status) {
      case 'At Sea':
        return { className: 'badge', style: { backgroundColor: '#D5F5F6' }, image: '/media/icons/duotune/status/ship.svg', label: 'At Sea' }
      case 'Anchor':
        return { className: 'badge', style: { backgroundColor: '#FDF6B2' }, image: '/media/icons/duotune/status/anchor.svg', label: 'Anchor' }
      case 'In Port':
        return { className: 'badge', style: { backgroundColor: '#A5A6F6' }, image: '/media/icons/duotune/status/port.svg', label: 'In Port' }
      default:
        return { className: 'badge bg-secondary text-white', image: '/media/icons/unknown.svg', label: status ?? '' }
    }
  };

  const formatEta = (eta: string | null) => {
    if (!eta || eta === '--:--') return '--';
    const d = new Date(eta);
    if (isNaN(d.getTime())) return eta;
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  // Group vessels by companyGroupAdminId for the accordion display
  const groupedByGroup = summary.reduce<Record<string, FleetSummaryItem[]>>((acc, item) => {
    const key = String(item.companyGroupAdminId ?? 'ungrouped');
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const groupKeys = Object.keys(groupedByGroup);
  const hasMultipleGroups = groupKeys.length > 1;

  const getGroupLabel = (key: string, items: FleetSummaryItem[]) => {
    if (key === 'ungrouped') return 'Ungrouped';
    return items[0]?.companyGroupAdminName || `Group ${key}`;
  };

  // Open first group by default once data loads
  useEffect(() => {
    if (groupKeys.length > 0 && Object.keys(openGroups).length === 0) {
      setOpenGroups({ [groupKeys[0]]: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKeys.length]);

  const renderVesselRow = (item: FleetSummaryItem) => {
    const statusCfg = getStatusConfig(item.status);
    return (
      <li
        key={item.id}
        className="list-group-item px-0"
        style={{ backgroundColor: '#F4F9FF', maxWidth: '100%' }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <span
            className="fw-bold text-truncate"
            title={item.fleetName}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%', minWidth: 0 }}
          >
            <VesselLandingLink
              vesselId={item.id}
              label={item.fleetName}
              className="text-dark text-decoration-underline"
            />
          </span>

          {statusCfg.label && statusCfg.label !== '--:--' && (
            <div
              className={`d-flex align-items-center gap-1 ${statusCfg.className}`}
              style={{ ...statusCfg.style, minWidth: 0, maxWidth: '40%' }}
            >
              {statusCfg.image && (
                <img src={statusCfg.image} alt={statusCfg.label} className="me-1" style={{ height: '14px', flexShrink: 0 }} />
              )}
              <span
                className="text-truncate"
                title={statusCfg.label}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
              >
                {statusCfg.label}
              </span>
            </div>
          )}
        </div>
        <div className="vessel_details">Next port: {item.nextPort && item.nextPort !== '--:--' ? item.nextPort : '--'}</div>
        <div className="vessel_details">ETA: {formatEta(item.eta)}</div>
      </li>
    );
  };

  if (loading) {
    return (
      <div className="h-full w-full bg-white" style={{ overflowY: 'hidden' }}>
        <div
          className="d-flex justify-content-between align-items-center pe-3 gap-3 pt-4 placeholder-wave"
          style={{ paddingLeft: '1.5rem', paddingRight: '1.25rem' }}
        >
          <div className="placeholder rounded" style={{ width: '40%', height: '18px' }} />
          <div className="placeholder rounded" style={{ width: '20%', height: '14px' }} />
        </div>
        <div style={{ paddingLeft: '1.5rem', paddingRight: '1rem', marginTop: '1.5rem' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="placeholder-wave p-3 rounded mb-3"
              style={{ backgroundColor: '#F4F9FF', border: '1px solid #E5E7EB' }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="placeholder rounded" style={{ width: '55%', height: '14px' }} />
                <div className="placeholder rounded" style={{ width: '25%', height: '14px' }} />
              </div>
              <div className="placeholder rounded w-75 mb-1" style={{ height: '10px' }} />
              <div className="placeholder rounded w-50" style={{ height: '10px' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-white">
      <div
        className="d-flex justify-content-between align-items-center pe-3 gap-3 pt-4"
        style={{ paddingLeft: '1.5rem', paddingRight: '1.25rem' }}
      >
        <h5 className="m-0">Vessel List</h5>
        <p className="m-0" style={{ color: '#6B7280', fontWeight: '400' }}>
          {summary.length} {summary.length === 1 ? 'Vessel' : 'Vessels'}
        </p>
      </div>

      <div style={{ paddingLeft: '1.5rem', paddingRight: '1rem', marginTop: '0.75rem' }}>
        {error ? (
          <div className="alert alert-danger p-3 my-2" role="alert">
            <div className="small fw-semibold text-danger mb-2">{error}</div>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger py-1 px-3"
              onClick={() => fetchSummary()}
              disabled={loading}
            >
              Retry
            </button>
          </div>
        ) : summary.length === 0 ? (
          <p className="text-muted small">No vessels found.</p>
        ) : hasMultipleGroups ? (
          // Accordion view — React-controlled, no Bootstrap JS dependency
          <div>
            {groupKeys.map((key) => {
              const items = groupedByGroup[key];
              const groupLabel = getGroupLabel(key, items);
              const isOpen = !!openGroups[key];
              return (
                <div
                  key={key}
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header row */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(key)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 14px',
                      background: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: '#1E3A5F',
                      fontSize: '13px',
                      textAlign: 'left',
                    }}
                    aria-expanded={isOpen}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {groupLabel}
                    </span>
                    {/* Chevron — rotates when open */}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{
                        flexShrink: 0,
                        transition: 'transform 0.2s ease',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    >
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* Collapsible body */}
                  {isOpen && (
                    <div style={{ borderTop: '1px solid #E5E7EB' }}>
                      <ul className="list-group">{items.map(renderVesselRow)}</ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          // Flat list for single-group users (company admin, crew, etc.)
          <ul className="list-group">{summary.map(renderVesselRow)}</ul>
        )}
      </div>
    </div>
  );
};

export default VesselsView;
